import { parseProbeLine, parseProposalLine } from '../parse.js';
import type { ParsedVerdict } from '../parse.js';

/**
 * What the crawl turned back, recovered from the job's own log.
 *
 * The public console has always shown this, because it watches the SSE stream
 * and keeps every verdict as it arrives. The dashboard polls the durable job
 * record instead, and that record carries the same lines — the runner pipes the
 * crawler's stdout into `job.log` verbatim — so nothing new has to be sent for
 * a signed-in customer to see what a stranger already sees. This module is the
 * bridge, and it parses the format the crawler already prints rather than
 * asking the server for a second, structured one:
 *
 *   `   · list_items (heuristic)`                       stage 4 proposed it
 *   `   PASS  list_items  rows=49 pass=100%`            the gate executed it
 *   `   DROP  query_x  rows=0 pass=0%  — <why>`         the gate turned it back
 *   `   OFF   news_list (score -3) — <why>`             shipped switched off
 *   `                · https://…/x (rank 14)`           queued, never fetched
 *
 * The last two are new, and they are the reason a customer opens a site and
 * finds three tools where the log says twelve were proposed: the crawler now
 * ships a weak tool with its surfaces switched off rather than deleting it, and
 * it records the pages it ran out of budget before reaching. Both facts are
 * only ever read together with the tool count — "four tools" means something
 * different when the next line is "and the outage lookup was two pages down the
 * queue" — so they are recovered in one pass and rendered in one place.
 *
 * Nothing here infers. A log that carries none of these lines produces an
 * outcome with `any` false, and the screen says the crawl left no record rather
 * than drawing an empty ledger that reads as "nothing was refused".
 */

/** A tool the crawl scored below its threshold and shipped with its surfaces off. */
export interface RankedLow {
  id: string;
  score: number;
  /** The first signal that counted against it, in the crawler's own words. */
  reason: string;
}

/** A page the ranker queued and the budget never reached. */
export interface Unreached {
  url: string;
  /** The corpus ranker's score. Higher is a page more worth spending a fetch on. */
  rank: number;
}

export interface CrawlOutcome {
  /** Ids stage 4 proposed, in the order it proposed them. */
  proposed: string[];
  /** Every PASS/DROP verdict, in log order. */
  executed: ParsedVerdict[];
  cleared: ParsedVerdict[];
  turnedBack: ParsedVerdict[];
  rankedLow: RankedLow[];
  /**
   * The unreached pages the crawler actually named. It prints at most five of
   * them however many it missed, so this is a sample and never the count.
   */
  unreached: Unreached[];
  /**
   * How many it missed, which is the number a screen may state.
   *
   * The CLI says it once — `out of budget 12 page(s) …` — and then lists five.
   * Counting the bullets instead would have printed "5 pages never fetched"
   * under a recommendation to raise the budget by twelve, which is the kind of
   * arithmetic that makes a person distrust every other number on the screen.
   */
  unreachedTotal: number;
  /** The `--pages` value the crawler itself recommended, when it said one. */
  suggestedPages: number | null;
  /** False when the log carried nothing to render. Never draw an empty ledger. */
  any: boolean;
}

/** `   OFF   news_list (score -3) — the site's own furniture` */
const OFF = /^\s*OFF\s+([a-z][a-z0-9_]*)\s+\(score\s+(-?\d+)\)\s*(?:[—–-]\s*(.+))?$/i;

/**
 * The CLI's own summary of the same fact, kept as a fallback.
 *
 * `                · news_list                    score   -3  the site's own furniture`
 *
 * The pipeline line above is the better one and wins where both are present;
 * this exists because the two are printed by different files and a crawl run
 * through the container queue has been known to carry one without the other.
 */
const OFF_BULLET = /^\s*[·•]\s+([a-z][a-z0-9_]*)\s+score\s+(-?\d+)\s{2,}(.+)$/i;

/** `                · https://example.com/outages (rank 14)` */
const UNREACHED = /^\s*[·•]\s+(https?:\/\/\S+)\s+\(rank\s+(-?\d+(?:\.\d+)?)\)\s*$/i;

/**
 * `out of budget 3 page(s) a person acts on … — re-run with --pages 24:`
 *
 * Both numbers are taken from this one line: the count, because only five of
 * the pages are then listed, and the recommendation, because it is the crawler's
 * own arithmetic over its own budget and nothing here can reproduce it.
 */
const PAGES_HINT = /out of budget\s+(\d+)\s+page\(s\).*--pages\s+(\d+)/i;

export function readCrawlOutcome(log: readonly string[]): CrawlOutcome {
  const proposed: string[] = [];
  const seenProposal = new Set<string>();
  const executed: ParsedVerdict[] = [];
  const ranked = new Map<string, RankedLow>();
  const unreached: Unreached[] = [];
  let declaredMissed: number | null = null;
  let suggestedPages: number | null = null;

  for (const line of log) {
    const verdict = parseProbeLine(line);
    if (verdict) {
      executed.push(verdict);
      continue;
    }

    const off = OFF.exec(line);
    if (off) {
      ranked.set(off[1] ?? '', {
        id: off[1] ?? '',
        score: Number(off[2] ?? 0),
        reason: off[3]?.trim() || 'nothing scored in its favour',
      });
      continue;
    }

    const miss = UNREACHED.exec(line);
    if (miss) {
      unreached.push({ url: miss[1] ?? '', rank: Number(miss[2] ?? 0) });
      continue;
    }

    const bullet = OFF_BULLET.exec(line);
    if (bullet) {
      const id = bullet[1] ?? '';
      if (!ranked.has(id)) {
        ranked.set(id, { id, score: Number(bullet[2] ?? 0), reason: (bullet[3] ?? '').trim() });
      }
      continue;
    }

    const hint = PAGES_HINT.exec(line);
    if (hint) {
      declaredMissed = Number(hint[1]);
      suggestedPages = Number(hint[2]);
      continue;
    }

    // Last, because a proposal line is the loosest pattern here and every
    // stricter one above would otherwise be read as a proposal called `rank`.
    const id = parseProposalLine(line);
    if (id && !seenProposal.has(id)) {
      seenProposal.add(id);
      proposed.push(id);
    }
  }

  const cleared = executed.filter((c) => c.verdict === 'cleared');
  const turnedBack = executed.filter((c) => c.verdict === 'turned-back');
  const rankedLow = [...ranked.values()];

  return {
    proposed,
    executed,
    cleared,
    turnedBack,
    rankedLow,
    unreached,
    // The listed bullets are the floor: a log truncated between the sentence
    // and its list would otherwise report fewer missed pages than it shows.
    unreachedTotal: Math.max(declaredMissed ?? 0, unreached.length),
    suggestedPages,
    any:
      proposed.length > 0 ||
      executed.length > 0 ||
      rankedLow.length > 0 ||
      unreached.length > 0 ||
      (declaredMissed ?? 0) > 0,
  };
}

/**
 * How many tools were proposed, counted the way a person would count them.
 *
 * The proposal lines are the honest source, but a log truncated at the head —
 * or a crawl whose synthesis stage printed nothing this parser recognises —
 * still carries a verdict per capability, and a gate that judged twelve things
 * was handed twelve things. Taking the larger of the two is what stops the
 * ledger claiming "3 proposed, 12 executed".
 */
export function proposedCount(outcome: CrawlOutcome): number {
  return Math.max(outcome.proposed.length, outcome.executed.length);
}

/**
 * The pages worth raising `--pages` for, best first.
 *
 * The crawler already filtered these to the ones its ranker thinks a person
 * does something on before printing them, so the order is the ranker's and the
 * only thing left to decide is how many of them a screen shows.
 */
export function topUnreached(outcome: CrawlOutcome, limit = 5): Unreached[] {
  return [...outcome.unreached].sort((a, b) => b.rank - a.rank).slice(0, limit);
}
